import React from 'react';

import { IGuest, IRoom } from '@share/common-types';
import { IActivityAgeBand, IAgeRange, getRoomPickerAgeConfig } from '@common-types';
import { getEmptyKid } from '@share/utils';
import { CustomSelect } from '@share/components';

import { Counts } from '../counts';

import { faBaby, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './style.scss';

interface IProps {
  room: IRoom;
  index: number;
  haveScroll: boolean;
  /** When set (e.g. from activity details), restricts ages and messages; minAge >= 18 hides children. */
  ageRange?: IAgeRange;
  /** When set with `ageBands` from the activity, overrides child/adult numeric bounds. */
  ageBands?: IActivityAgeBand[] | null;
  updateAdultsCount: (roomId: string, count: number) => void;
  updateAdultAge: (roomId: string, index: number, age: number | null) => void;
  addKid: (roomId: string, kid: IGuest) => void;
  removeKid: (roomId: string, kidId: string) => void;
  removeRoom: (roomId: string) => void;
  updateKidAge: (roomId: string, kidId: string, age: number | null) => void;
}

export const Room: React.FC<IProps> = ({
  room,
  addKid,
  removeKid,
  updateAdultsCount,
  updateAdultAge,
  updateKidAge,
  ageRange,
  ageBands,
}) => {
  const { id = '', kids = [], adultsCount = 0, adultAges = [] } = room || {};

  const ONE = 1;
  const GUEST_MAX_COUNT = 8;

  const { minAdultAge, maxAdultAge, childMinAge, childMaxAge, showChildren, fromAgeBands } = getRoomPickerAgeConfig(ageRange, ageBands);

  const adultsDescription = (() => {
    if (fromAgeBands && maxAdultAge != null) {
      return `${minAdultAge} to ${maxAdultAge} years old`;
    }
    if (fromAgeBands) {
      return `${minAdultAge} years old or more`;
    }
    if (ageRange) {
      if (ageRange.minAge >= 18) {
        return ageRange.minAge === 18
          ? 'Over 17 years old'
          : `${ageRange.minAge} years old or more`;
      }
      return '17 years old or more';
    }
    return 'Over 17 years old';
  })();

  const childrenDescription = showChildren
    ? `${childMinAge} to ${childMaxAge} years old`
    : '';

  const lastKid = kids[kids.length - ONE];
  const maxAdultsRemaining = GUEST_MAX_COUNT - kids.length;
  const maxChildRemaining = GUEST_MAX_COUNT - adultsCount;

  const isAdultAgeInvalid = (age: number | null | undefined) => {
    if (age === undefined || age === null) return true;
    if (age < minAdultAge) return true;
    if (maxAdultAge != null && age > maxAdultAge) return true;
    return false;
  };

  const isChildAgeInvalid = (age: number | null | undefined) => {
    if (age === undefined || age === null) return true;
    return age < childMinAge || age > childMaxAge;
  };

  const childAgeOptions = Array.from(
    { length: childMaxAge - childMinAge + 1 },
    (_, i) => ({ label: childMinAge + i, value: childMinAge + i })
  );

  return (
    <div className="activity-room" key={id}>
      {/* -------- Adults Section -------- */}
      <div className="activity-room__field">
        <div className="activity-room__user-icon">
          <FontAwesomeIcon icon={faUser} style={{ color: '#D2D2D2', fontSize: 16 }} />
        </div>

        <div className="activity-room__description">
          <div className="activity-room__user-label">Adults</div>
          <div className="activity-room__user-description">{adultsDescription}</div>
        </div>

        <div className="activity-room__counts">
          <Counts
            count={adultsCount}
            add={() => updateAdultsCount(id || '', adultsCount + ONE)}
            minus={() => updateAdultsCount(id || '', adultsCount - ONE)}
            min={1}
            max={maxAdultsRemaining}
          />
        </div>
      </div>

      {/* -------- Children Section (hidden when adult-only or min age >= 18) -------- */}
      {showChildren && (
        <div className="activity-room__field">
          <div className="activity-room__user-icon">
            <FontAwesomeIcon icon={faBaby} style={{ color: '#D2D2D2', fontSize: 18 }} />
          </div>

          <div className="activity-room__description">
            <div className="activity-room__user-label">Children</div>
            <div className="activity-room__user-description">{childrenDescription}</div>
          </div>

          <div className="activity-room__counts">
            <Counts
              count={kids.length}
              add={() => addKid(id || '', { ...getEmptyKid(), age: childMinAge })}
              minus={() => removeKid(id || '', lastKid?.id || '')}
              min={0}
              max={maxChildRemaining}
            />
          </div>
        </div>
      )}

      {/* -------- Adult Ages Section -------- */}
      {adultsCount > 0 && (
        <>
          <div className="activity-room__kids-selects-title">Adult Ages</div>

          <div className="activity-room__kids-selects">
            {Array.from({ length: adultsCount }).map((_, idx) => (
              <div key={idx} className="activity-room__adult-age">
                <label className="activity-room__kids-select-label">
                  Adult {idx + 1}
                </label>

                <input
                  type="number"
                  min={minAdultAge}
                  {...(maxAdultAge != null && { max: maxAdultAge })}
                  step="1"
                  placeholder="Age"
                  value={adultAges[idx] ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === ',') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const rawVal = e.target.value;
                    const val = rawVal === '' ? null : Math.trunc(Number(rawVal));
                    updateAdultAge(id || '', idx, val);
                  }}
                  className={`activity-room__adult-age-input ${isAdultAgeInvalid(adultAges[idx])
                    ? 'activity-room__adult-age-input--error' : ''
                    }`}
                />
                {(adultAges[idx] === undefined || adultAges[idx] === null) && (
                  <span className="activity-room__adult-age-error">Please enter the age</span>
                )}
                {adultAges[idx] !== undefined && adultAges[idx] !== null && isAdultAgeInvalid(adultAges[idx]) && (
                  <span className="activity-room__adult-age-error">
                    Please enter a valid age ({minAdultAge}
                    {maxAdultAge != null ? `-${maxAdultAge}` : ' or more'})
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* -------- Children Ages -------- */}
      {kids.length > 0 && (
        <>
          <div className="kids-age__field">
            <div className="activity-room__kids-selects-title">Children Ages</div>

            <div className="activity-room__kids-selects">
              {kids.map((kid, idx) => {
                const kidAge = kid.age;
                const displayAge =
                  kidAge != null && !isChildAgeInvalid(kidAge)
                    ? kidAge
                    : childMinAge;
                return (
                  <div key={kid.id} className="activity-room__child-age-wrapper">
                    <CustomSelect
                      value={displayAge}
                      onChange={(value: unknown) =>
                        updateKidAge(id || '', kid.id || '', Number(value))
                      }
                      options={childAgeOptions}
                      label={
                        <div className="activity-room__kids-select-label">
                          Child {idx + 1}
                        </div>
                      }
                    />
                    {isChildAgeInvalid(kidAge) && (
                      <span className="activity-room__adult-age-error">
                        Please enter a valid age ({childMinAge}-{childMaxAge})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
